import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useCart } from '../context/CartContext.jsx'
import { Button } from './primitives/index.js'
import './CartSidebar.css'

export default function CartSidebar() {
  const {
    lines, isOpen, setIsOpen, removeItem, updateQty,
    subtotal, bulkSavings, discountCode, discountAmount, discountError,
    shipping, total, applyDiscount, removeDiscount,
  } = useCart()
  const navigate = useNavigate()
  const [codeInput, setCodeInput] = useState('')

  function handleApply() {
    if (!codeInput.trim()) return
    applyDiscount(codeInput.trim())
  }

  function goToCheckout() {
    setIsOpen(false)
    navigate('/checkout')
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="cart-sidebar__scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          />
          <motion.aside
            className="cart-sidebar"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="cart-sidebar__header">
              <h3>Your Cart</h3>
              <button className="cart-sidebar__close" onClick={() => setIsOpen(false)} aria-label="Close cart">
                &times;
              </button>
            </div>

            {lines.length === 0 ? (
              <div className="cart-sidebar__empty">
                <p>Your cart is empty.</p>
                <Button variant="secondary" onClick={() => setIsOpen(false)}>
                  Continue browsing
                </Button>
              </div>
            ) : (
              <>
                <div className="cart-sidebar__items">
                  <AnimatePresence initial={false}>
                    {lines.map((item) => (
                      <motion.div
                        key={item.lineId}
                        className="cart-sidebar__item"
                        layout
                        initial={{ opacity: 0, x: 24 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 24, height: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 }}
                      >
                        <div className="cart-sidebar__item-info">
                          <span className="cart-sidebar__item-name">{item.name}</span>
                          <span className="cart-sidebar__item-size">{item.labelSize ?? item.concentration}</span>
                          {item.bulkApplied && (
                            <span className="cart-sidebar__bulk-badge">Bulk price &times; {item.qty}</span>
                          )}
                          <div className="cart-sidebar__qty">
                            <button onClick={() => updateQty(item.lineId, item.qty - 1)} aria-label="Decrease quantity">
                              &minus;
                            </button>
                            <span>{item.qty}</span>
                            <button onClick={() => updateQty(item.lineId, item.qty + 1)} aria-label="Increase quantity">
                              +
                            </button>
                          </div>
                        </div>
                        <div className="cart-sidebar__item-right">
                          <span>${(item.effectivePrice * item.qty).toFixed(2)}</span>
                          <button className="cart-sidebar__remove" onClick={() => removeItem(item.lineId)} aria-label="Remove item">
                            Remove
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                <div className="cart-sidebar__footer">
                  <div className="cart-sidebar__code">
                    <input
                      type="text"
                      className="cart-sidebar__code-input"
                      placeholder="Discount code"
                      value={discountCode ? discountCode.code : codeInput}
                      onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                      disabled={!!discountCode}
                      onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                    />
                    {discountCode ? (
                      <button type="button" onClick={() => { removeDiscount(); setCodeInput('') }}>Remove</button>
                    ) : (
                      <button type="button" onClick={handleApply}>Apply</button>
                    )}
                  </div>
                  {discountCode && <p className="cart-sidebar__code-msg">&#10003; {discountCode.description}</p>}
                  {discountError && <p className="cart-sidebar__code-error">{discountError}</p>}

                  <div className="cart-sidebar__totals">
                    <div>
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    {bulkSavings > 0 && (
                      <div className="cart-sidebar__totals-savings">
                        <span>Bulk savings</span>
                        <span>&minus;${bulkSavings.toFixed(2)}</span>
                      </div>
                    )}
                    {discountAmount > 0 && (
                      <div className="cart-sidebar__totals-savings">
                        <span>Discount ({discountCode?.code})</span>
                        <span>&minus;${discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div>
                      <span>Shipping</span>
                      <span>{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
                    </div>
                  </div>

                  <div className="cart-sidebar__subtotal">
                    <span>Total</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                  <Button variant="primary" size="lg" onClick={goToCheckout} className="cart-sidebar__checkout">
                    Checkout
                  </Button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
